import React, { createContext, useContext, useEffect, useMemo, useState, useRef, useCallback } from "react";

/**
 * DemoContext — single source of truth for "is the app currently running an
 * auto-play demonstration?". Screens read `demo.active` and, when true,
 * auto-fill themselves + call their onComplete after a configurable pause.
 */
const DemoContext = createContext({
  active: false,
  step: "",
  start: () => {},
  stop: () => {},
  setStep: () => {},
});

export const useDemo = () => useContext(DemoContext);

// Helper hook for screens — sleep ms, then run cb if still active.
export const useDemoStep = (cb, ms = 2200) => {
  const demo = useDemo();
  const ranRef = useRef(false);
  const cbRef = useRef(cb);
  useEffect(() => {
    cbRef.current = cb;
  });
  useEffect(() => {
    if (!demo.active || ranRef.current) return;
    ranRef.current = true;
    setTimeout(() => {
      if (demo.active) cbRef.current();
    }, ms);
    // No cleanup: in React Strict Mode the effect double-mounts and cleanup
    // would clear the timer before it fires. The callback itself checks
    // demo.active, so a stale timer is harmless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo.active]);
};

export const DemoProvider = ({ children }) => {
  const [active, setActive] = useState(false);
  const [step, setStep] = useState("");

  const start = useCallback(() => {
    setActive(true);
    setStep("Starting demo…");
  }, []);
  const stop = useCallback(() => {
    setActive(false);
    setStep("");
  }, []);

  // Memoize the context value so consumers don't re-render every time
  // DemoProvider re-renders for an unrelated reason.
  const value = useMemo(
    () => ({ active, step, start, stop, setStep }),
    [active, step, start, stop]
  );

  return (
    <DemoContext.Provider value={value}>
      {children}
      {active && (
        <DemoBanner
          step={step}
          onStop={() => {
            stop();
            window.dispatchEvent(new Event("demo-exit"));
          }}
        />
      )}
    </DemoContext.Provider>
  );
};

function DemoBanner({ step, onStop }) {
  return (
    <div
      className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 rounded-full"
      style={{
        background: "rgba(15,26,46,0.96)",
        color: "#fff",
        boxShadow: "0 14px 40px rgba(15,26,46,0.30), 0 0 0 1px rgba(255,255,255,0.06) inset",
        backdropFilter: "blur(10px)",
        pointerEvents: "none",
      }}
      data-testid="demo-banner"
    >
      <span
        className="w-2 h-2 rounded-full"
        style={{
          background: "var(--accent-red)",
          boxShadow: "0 0 14px rgba(187,27,27,0.7)",
          animation: "pulseGlow 1.4s ease-in-out infinite",
        }}
      />
      <span className="text-[11px] tracking-[0.28em] uppercase font-semibold">
        Demo Mode
      </span>
      <span className="text-sm text-white/85 hidden sm:inline">· {step}</span>
      <button
        type="button"
        onClick={onStop}
        className="ml-2 text-[11px] tracking-widest uppercase font-semibold px-3 py-1 rounded-full"
        style={{
          background: "rgba(255,255,255,0.10)",
          color: "#fff",
          border: "1px solid rgba(255,255,255,0.18)",
          pointerEvents: "auto",
        }}
        data-testid="demo-stop-btn"
      >
        Stop
      </button>
    </div>
  );
}
